import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import {Button} from "primereact/button";
import AdminDialog, {AdminDialogProperties, AdminDialogState} from "./AdminDialog";
import AdminDialogType from "../AdminDialogType";
import Message from "../../../shared/log/Message";
import {MessageType} from "../../../shared/log/MessageInterface";
import {Dropdown} from "primereact/dropdown";
import DesignDialogFields from "../field/DesignDialogFields";
import FileInput, {File} from "../../../shared/components/FileInput";
import {BackgroundType, DesignType, LogoType, ThemeType} from "../../../shared/models/DesignType";
import Administration from "../../session/Administration";
import emptyImage from "../../../../resources/images/empty.png";
import MessageBox from "../../../shared/components/MessageBox";
import Translate from "../../../shared/translation/Translate";
import Helper from "../../../shared/components/Helper";
import Icon from "../../../shared/components/Icon";
import FunctionHelper from "../../../shared/helper/FunctionHelper";
import {
	BackgroundFileDataStore,
	BackgroundFileDataStoreInterface,
	FileGroupDataStore,
	LogoFileDataStore,
	LogoFileDataStoreInterface,
	ThemeFileDataStore,
	ThemeFileDataStoreInterface
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import Info, {InfoType} from "../../../shared/components/Info";
import portalConfiguration from "../../../shared/configuration/PortalConfiguration";
import CodeInput from "../../../shared/components/CodeInput";

interface DesignDialogProperties extends AdminDialogProperties<DesignType> {
}

interface DesignDialogStatus extends AdminDialogState<DesignType> {
	logoFile: File;
	backgroundFile: File;
}

export class DesignDialogDefinition extends AdminDialog<DesignType, DesignDialogProperties,
	DesignDialogStatus> {
	constructor(props: Readonly<DesignDialogProperties>) {
		super(props);

		this.state = {
			...this.state,
			logoFile: {
				name: "",
				mimetype: "",
				value: ""
			},
			backgroundFile: {
				name: "",
				mimetype: "",
				value: ""
			}
		};
	}

	public renderContents(): ReactNode {
		let parameter: DesignType | undefined = this.state.parameter;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		return (
			<>
				<MessageBox {...this.props} type={MessageType.INFO}>
					<Translate {...this.props} data="design.info" namespace="dialog"/>
				</MessageBox>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-grid">
						<label className="p-col-12 p-sm-4" htmlFor="control-theme">
							<Translate {...this.props} data="design.theme.label" namespace="dialog"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={DesignType.getThemeTypeDescription()}/>
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<Dropdown inputId="control-theme"
									  value={FunctionHelper.applyValue(
										  parameter?.themeType, DesignType.getThemeTypeDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "design.theme.default", {ns: "dialog"}
											  ),
											  value: ThemeType.DEFAULT
										  },
										  {
											  label: Translate.value("design.theme.red", {ns: "dialog"}),
											  value: ThemeType.RED
										  }
										  ,
										  {
											  label: Translate.value("design.theme.green", {ns: "dialog"}),
											  value: ThemeType.GREEN
										  }
										  ,
										  {
											  label: Translate.value("design.theme.blue", {ns: "dialog"}),
											  value: ThemeType.BLUE
										  }
										  ,
										  {
											  label: Translate.value("design.theme.dark", {ns: "dialog"}),
											  value: ThemeType.DARK
										  }
										  ,
										  {
											  label: Translate.value("design.theme.custom", {ns: "dialog"}),
											  value: ThemeType.CUSTOM
										  }
									  ]}
									  onChange={(e) => this.onChange(DesignDialogFields.THEME, e.value)}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-grid" hidden={parameter?.themeType !== ThemeType.CUSTOM}>
						<CodeInput inputId="control-theme-textarea" language={"css"} accept={".css"} height={"400px"}
								   value={FunctionHelper.applyValue(
									   parameter?.theme, DesignType.getThemeDefault()
								   )}
								   options={{
									   contextmenu: false,
									   minimap: {
										   enabled: false
									   }
								   }}
								   onChange={(e) => this.onChange(
									   DesignDialogFields.THEME_CONTENT, e.value
								   )}/>
					</div>
					<div className="p-col-12 p-grid">
						<label className="p-col-12 p-sm-4" htmlFor="control-logo">
							<Translate {...this.props} data="design.logo.label" namespace="dialog"/>
							{FunctionHelper.isEmpty(DesignType.getLogoTypeDescription()) ?
								<Info {...this.props} type={InfoType.Help} markdown={true}
									  data={"dialog.admin.design.logo"} options={{ns: "help"}}/> :
								<Info {...this.props} type={InfoType.Help} markdown={true}
									  data={DesignType.getLogoTypeDescription()}/>
							}
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<Dropdown inputId="control-logo"
									  value={FunctionHelper.applyValue(
										  parameter?.logoType, DesignType.getLogoTypeDefault()
									  )}
									  options={[
										  {
											  label: Translate.value("design.logo.default", {ns: "dialog"}),
											  value: LogoType.DEFAULT
										  },
										  {
											  label: Translate.value("design.logo.custom", {ns: "dialog"}),
											  value: LogoType.CUSTOM
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  DesignDialogFields.LOGO, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-grid" hidden={parameter?.logoType !== LogoType.CUSTOM}>
						<label className="p-col-12 p-sm-4" htmlFor="control-logo-file">
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={DesignType.getLogoDescription()}/>
						</label>
						<div className="p-col-12 p-sm-8">
							<FileInput inputId="control-logo-file" accept="image/gif, image/png, image/jpeg"
									   value={FunctionHelper.applyValue(
										   this.state.logoFile.value, DesignType.getLogoDefault
									   )} filename={this.state.logoFile.name}
									   onChange={(files) => this.onChange(
										   DesignDialogFields.LOGO_FILE, files[0]
									   )}/>
						</div>
					</div>
					<div className="p-col-12 p-grid">
						<label className="p-col-12 p-sm-4" htmlFor="control-background">
							<Translate {...this.props} data="design.background.label" namespace="dialog"/>
							<Info {...this.props} type={InfoType.Help} markdown={true}
								  data={DesignType.getBackgroundTypeDescription()}/>
							{FunctionHelper.isEmpty(DesignType.getBackgroundTypeDescription()) ?
								<Info {...this.props} type={InfoType.Help} markdown={true}
									  data={"dialog.admin.design.background"} options={{ns: "help"}}/> :
								<Info {...this.props} type={InfoType.Help} markdown={true}
									  data={DesignType.getBackgroundTypeDescription()}/>
							}
						</label>
						<div className="p-col-12 p-sm-8 p-grid">
							<Dropdown inputId="control-background"
									  value={FunctionHelper.applyValue(
										  parameter?.backgroundType, DesignType.getBackgroundTypeDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "design.background.default", {ns: "dialog"}
											  ),
											  value: BackgroundType.DEFAULT
										  },
										  {
											  label: Translate.value(
												  "design.background.custom", {ns: "dialog"}
											  ),
											  value: BackgroundType.CUSTOM
										  },
										  {
											  label: Translate.value(
												  "design.background.hide", {ns: "dialog"}
											  ),
											  value: BackgroundType.HIDDEN
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  DesignDialogFields.BACKGROUND, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={parameter?.backgroundType !== BackgroundType.CUSTOM || parameter?.hideBackground}>
						<label className="p-col-12 p-sm-4" htmlFor="control-background-file"/>
						<div className="p-col-12 p-sm-8">
							<FileInput inputId="control-background-file" accept="image/gif, image/png, image/jpeg"
									   value={FunctionHelper.applyValue(
										   this.state.backgroundFile.value, DesignType.getBackgroundDefault()
									   )} filename={this.state.backgroundFile.name}
									   onChange={(files) => this.onChange(
										   DesignDialogFields.BACKGROUND_FILE, files[0]
									   )}/>
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["design-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()} resizable={false}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.admin.design.general"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-design"}/>
					<Translate {...this.props} data="design.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected onChange(field: any, value: any): void {
		let parameter: DesignType = this.state.parameter as DesignType;

		switch (field) {
			case DesignDialogFields.LOGO:
				parameter.logoType = value;
				break;
			case DesignDialogFields.LOGO_FILE:
				if (!FunctionHelper.exists(value)) {
					return;
				}

				let logoFile: File = value;
				parameter.logo = logoFile.value;

				this.setState({
					logoFile: logoFile
				});
				break;
			case DesignDialogFields.THEME:
				parameter.themeType = value;
				break;
			case DesignDialogFields.THEME_CONTENT:
				parameter.theme = value;
				break;
			case DesignDialogFields.BACKGROUND:
				parameter.hideBackground = value === BackgroundType.HIDDEN;
				parameter.backgroundType = value;
				break;
			case DesignDialogFields.BACKGROUND_FILE:
				if (!FunctionHelper.exists(value)) {
					return;
				}

				let backgroundFile: File = value;
				parameter.background = backgroundFile.value;

				this.setState({
					backgroundFile: backgroundFile
				});
				break;
		}

		this.setState({
			parameter: this.state.parameter
		}, () => {
			super.onChange(field, value);
		});
	}

	protected async submit(): Promise<void> {
		await super.submit();

		let administration: Administration = this.props.context.session.getAdministration();
		let parameter: DesignType = this.state.parameter as DesignType;
		let originalParameter: DesignType = this.state.originalParameter as DesignType;

		try {
			switch (parameter.themeType) {
				case ThemeType.CUSTOM:
					await administration.updateDatastore(ThemeFileDataStore.fromJson({
						fileGroup: FileGroupDataStore.Theme,
						fileContent: parameter.theme
					} as ThemeFileDataStoreInterface));
					break;
				case ThemeType.RED:
				case ThemeType.BLUE:
				case ThemeType.GREEN:
				case ThemeType.DARK:
					await administration.updateDatastore(ThemeFileDataStore.fromJson({
						fileGroup: FileGroupDataStore.Theme,
						fileContent: "/*webpdf:theme " + parameter.themeType + "*/\n" +
							"@import \"../" + parameter.themeType + portalConfiguration.cachingPath + ".css\""
					} as ThemeFileDataStoreInterface));
					break;
				default:
					if (originalParameter.themeType !== ThemeType.DEFAULT) {
						await administration.deleteDatastore(FileGroupDataStore.Theme);
					}
					break;
			}

			switch (parameter.logoType) {
				case LogoType.CUSTOM:
					await administration.updateDatastore(LogoFileDataStore.fromJson({
						fileGroup: FileGroupDataStore.Logo,
						fileContent: parameter.logo
					} as LogoFileDataStoreInterface));
					break;
				default:
					if (originalParameter.logoType !== LogoType.DEFAULT) {
						await administration.deleteDatastore(FileGroupDataStore.Logo);
					}
					break;
			}

			switch (parameter.backgroundType) {
				case BackgroundType.CUSTOM:
					await administration.updateDatastore(BackgroundFileDataStore.fromJson({
						fileGroup: FileGroupDataStore.Background,
						fileContent: parameter.background
					} as BackgroundFileDataStoreInterface));
					break;
				case BackgroundType.HIDDEN:
					if (parameter.hideBackground) {
						await administration.updateDatastore(BackgroundFileDataStore.fromJson({
							fileGroup: FileGroupDataStore.Background,
							fileContent: emptyImage.substring(emptyImage.indexOf(',') + 1)
						} as BackgroundFileDataStoreInterface));
						break;
					}
					break;
				default:
					await administration.deleteDatastore(FileGroupDataStore.Background);
					break;
			}
		} catch (exception: any) {
			if (await this.onError(exception)) {
				await this.submit();
			}
			return;
		}

		let message: Message = new Message("success.administration.design", MessageType.SUCCESS);
		this.props.context.getPage().addMessage(message);
		this.hide();
	}

	protected getDialogType(): AdminDialogType {
		return AdminDialogType.DESIGN;
	}
}

const DesignDialog = React.memo(DesignDialogDefinition,
	(prevProps: Readonly<DesignDialogProperties>, nextProps: Readonly<DesignDialogProperties>) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default DesignDialog;